import { DateTime } from 'luxon'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { TouchEvent } from 'react'
import { useParams } from 'react-router'
import { ACCOUNT_PERMISSION } from '../../shared/constants'
import { ReservationWorkflowRequest, ReservationWorkflowRequestType } from '../../shared/types'
import { useHasAccountPermission } from '../account-permissions'
import { useObjectActionMutationVariable, useObjectList } from '../api/object'
import { Button } from '../components/button'
import { DateFormatter } from '../components/date-formatter'
import { Dialog, DialogFooter } from '../components/dialog'
import { LOADING } from '../components/loading'
import { Paper } from '../components/paper'
import { Textarea } from '../components/textarea'
import {
	FIELD_LABELS,
	FIELD_ORDER,
	REQUEST_TYPE_LABEL,
	STATUS_BADGE,
	STATUS_BORDER,
	STATUS_LABEL,
} from './const'

const IMAGE_URL_REG = /\.(png|jpe?g|webp|gif|bmp|heic|heif|svg)(\?.*)?$/i
const VEHICLE_PHOTO_KEY = 'car_photos'
const SWIPE_THRESHOLD_PX = 48
const normalizeRotation = (rotation: number) => ((rotation % 360) + 360) % 360
const PHOTO_ACTION_BUTTON_CLASS =
	'rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-200'
const PHOTO_ACTION_BUTTON_DARK_CLASS =
	'rounded-lg border border-slate-700 bg-slate-900/70 px-4 py-2 text-xs font-semibold text-slate-200 transition hover:border-slate-500 hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300'
const FUEL_HIDDEN_FIELDS = new Set([
	'fuel_level',
	'fuel_percent',
	'fuel_percentage',
	'fuel_gauge_percent',
	'fuel_ok',
])
const FUEL_HIDDEN_INNER_FIELDS = new Set(['percent', 'percentage', 'level', 'value', 'ok'])

const isFuelHiddenField = (field: string) => FUEL_HIDDEN_FIELDS.has(field)

const isFuelHiddenInnerField = (parentField = '', field: string) => {
	if (!FUEL_HIDDEN_INNER_FIELDS.has(field)) return false
	return ['fuel', 'fuel_gauge', 'fuelgauge'].includes(parentField.toLowerCase())
}

const clampPhotoIndex = (index: number, total: number) => {
	if (!total) return 0
	return ((index % total) + total) % total
}

const toFieldLabel = (field: string) => FIELD_LABELS[field] ?? field.replaceAll('_', ' ')
const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value)

const isLikelyImageUrl = (value: unknown) =>
	typeof value === 'string' &&
	(value.startsWith('data:image/') ||
		value.includes('/admin/uploads/') ||
		((value.startsWith('http://') || value.startsWith('https://') || value.startsWith('/')) &&
			IMAGE_URL_REG.test(value)))

const asPhotoList = (value: unknown) => {
	if (typeof value === 'string' && isLikelyImageUrl(value)) return [value]
	if (Array.isArray(value) && value.length && value.every(item => isLikelyImageUrl(item))) {
		return value
	}
	return null
}

const formatNumber = (value: number) => new Intl.NumberFormat('cs-CZ').format(value)

const isDateKey = (key = '') =>
	key.includes('date') || key.includes('time') || key.endsWith('_at') || key === 'timestamp'

const isAmountKey = (key = '') => {
	return (
		key.includes('amount') ||
		key.includes('price') ||
		key.includes('cena') ||
		key.includes('cost') ||
		key.includes('fee') ||
		key.includes('charge')
	)
}

const isPercentKey = (key = '') => {
	return [
		'fuel_level',
		'fuel_percent',
		'fuel_percentage',
		'fuel_gauge_percent',
		'stav_paliva',
		'percent',
		'percentage',
	].includes(key)
}

const isKmKey = (key = '') => {
	return (
		key === 'km' ||
		key.endsWith('_km') ||
		['diff', 'over', 'difference', 'overlimit_diff', 'overlimit_over'].includes(key)
	)
}

const formatDateTime = (value: string) => {
	const parsed = DateTime.fromISO(value)
	if (!parsed.isValid) return null
	return parsed.toFormat('d.M. HH:mm')
}

const getFormData = (payload: Record<string, unknown>): Record<string, unknown> => {
	if (isObjectRecord(payload?.data)) return payload.data as Record<string, unknown>
	if (isObjectRecord(payload)) return payload
	return {}
}

const formatScalarValue = (value: unknown, key = ''): string => {
	if (value === null || value === undefined) return 'neuvedeno'
	if (typeof value === 'boolean') return value ? 'Ano' : 'Ne'
	if (typeof value === 'number') {
		if (isKmKey(key)) return `${formatNumber(value)} km`
		if (isAmountKey(key)) {
			return `${formatNumber(value)} Kč`
		}
		if (isPercentKey(key)) {
			return `${formatNumber(value)} %`
		}
		return formatNumber(value)
	}
	if (typeof value === 'string') {
		const trimmed = value.trim()
		if (!trimmed.length) return 'neuvedeno'
		if (isDateKey(key)) {
			const formatted = formatDateTime(trimmed)
			if (formatted) return formatted
		}
		const normalized = trimmed.replace(/\s+/g, '').replace(',', '.')
		const parsed = Number(normalized)
		if (Number.isFinite(parsed)) {
			if (isKmKey(key)) return `${formatNumber(parsed)} km`
			if (isAmountKey(key)) {
				return `${formatNumber(parsed)} Kč`
			}
			if (isPercentKey(key)) {
				return `${formatNumber(parsed)} %`
			}
		}
		return trimmed
	}
	return 'neuvedeno'
}

function VehiclePhotosSlideshow({
	photos,
	keyPrefix,
	label = 'Fotodokumentace',
}: {
	photos: string[]
	keyPrefix: string
	label?: string
}) {
	const [activeIndex, setActiveIndex] = useState(0)
	const [isFullscreenOpen, setIsFullscreenOpen] = useState(false)
	const [rotationByIndex, setRotationByIndex] = useState<Record<number, number>>({})
	const touchStartX = useRef<number | null>(null)
	const total = photos.length

	useEffect(() => {
		setActiveIndex(previous => clampPhotoIndex(previous, total))
	}, [total])

	useEffect(() => {
		if (!isFullscreenOpen) return
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				setIsFullscreenOpen(false)
				return
			}
			if (event.key === 'ArrowRight') {
				setActiveIndex(previous => clampPhotoIndex(previous + 1, total))
				return
			}
			if (event.key === 'ArrowLeft') {
				setActiveIndex(previous => clampPhotoIndex(previous - 1, total))
			}
		}
		window.addEventListener('keydown', onKeyDown)
		return () => window.removeEventListener('keydown', onKeyDown)
	}, [isFullscreenOpen, total])

	const goTo = (index: number) => setActiveIndex(clampPhotoIndex(index, total))
	const currentPhoto = photos[clampPhotoIndex(activeIndex, total)]
	const currentPhotoRotation = rotationByIndex[clampPhotoIndex(activeIndex, total)] ?? 0
	const hasMultiple = total > 1

	const rotateCurrentPhoto = (delta: number) => {
		setRotationByIndex(previous => {
			const index = clampPhotoIndex(activeIndex, total)
			const nextValue = normalizeRotation((previous[index] ?? 0) + delta)
			const next = { ...previous }
			if (nextValue === 0) {
				delete next[index]
				return next
			}
			next[index] = nextValue
			return next
		})
	}

	const onTouchStart = (event: TouchEvent<HTMLDivElement>) => {
		const [firstTouch] = Array.from(event.changedTouches)
		touchStartX.current = firstTouch?.clientX ?? null
	}

	const onTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
		if (!hasMultiple || touchStartX.current === null) return
		const [firstTouch] = Array.from(event.changedTouches)
		if (!firstTouch) {
			touchStartX.current = null
			return
		}
		const shift = firstTouch.clientX - touchStartX.current
		if (Math.abs(shift) >= SWIPE_THRESHOLD_PX) {
			goTo(activeIndex + (shift < 0 ? 1 : -1))
		}
		touchStartX.current = null
	}

	if (!currentPhoto) return null

	return (
		<>
			<div className="space-y-3">
				<div className="flex flex-wrap items-center justify-between gap-2">
					<p className="text-xs font-semibold text-slate-500">{label}</p>
					<span className="rounded-full border border-slate-200 bg-white px-2 py-1 text-xxs font-semibold text-slate-600">
						{activeIndex + 1}/{total}
					</span>
				</div>

				<div
					className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 shadow-[0_22px_45px_-26px_rgba(15,23,42,0.9)]"
					onTouchStart={onTouchStart}
					onTouchEnd={onTouchEnd}
				>
					<div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(148,163,184,0.24),_transparent_58%)]" />
					<button
						type="button"
						onClick={() => setIsFullscreenOpen(true)}
						className="relative block w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
						aria-label="Otevřít fotku na celou obrazovku"
					>
						<img
							src={currentPhoto}
							alt={`foto vozidla ${activeIndex + 1}`}
							loading="lazy"
							className="h-72 w-full origin-center object-contain p-3 transition-transform duration-200 sm:h-80 sm:p-4"
							style={{
								transform: currentPhotoRotation
									? `rotate(${currentPhotoRotation}deg)`
									: undefined,
							}}
						/>
					</button>

					<div className="pointer-events-none absolute left-3 top-3 rounded-full bg-slate-950/70 px-2 py-1 text-xxs font-semibold text-slate-100 backdrop-blur">
						Foto {activeIndex + 1} z {total}
					</div>

					<div className="absolute bottom-3 right-3 rounded-full bg-white/90 px-2 py-1 text-xxs font-semibold text-slate-700 opacity-0 backdrop-blur transition group-hover:opacity-100">
						Celá obrazovka
					</div>

					{hasMultiple ? (
						<>
							<button
								type="button"
								onClick={() => goTo(activeIndex - 1)}
								className="absolute left-3 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-slate-900/75 text-sm font-bold text-white shadow-lg transition hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
								aria-label="Předchozí fotka"
							>
								&lt;
							</button>
							<button
								type="button"
								onClick={() => goTo(activeIndex + 1)}
								className="absolute right-3 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-slate-900/75 text-sm font-bold text-white shadow-lg transition hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
								aria-label="Další fotka"
							>
								&gt;
							</button>
						</>
					) : null}
				</div>

				<div className="flex flex-wrap items-center justify-end gap-2">
					<button
						type="button"
						onClick={() => rotateCurrentPhoto(-90)}
						className={PHOTO_ACTION_BUTTON_CLASS}
					>
						Otočit doleva
					</button>
					<button
						type="button"
						onClick={() => rotateCurrentPhoto(90)}
						className={PHOTO_ACTION_BUTTON_CLASS}
					>
						Otočit doprava
					</button>
					<button
						type="button"
						onClick={() => setIsFullscreenOpen(true)}
						className={PHOTO_ACTION_BUTTON_CLASS}
					>
						Celá obrazovka
					</button>
					<a
						href={currentPhoto}
						target="_blank"
						rel="noreferrer"
						className={PHOTO_ACTION_BUTTON_CLASS}
					>
						Otevřít originál
					</a>
				</div>

				{hasMultiple ? (
					<div className="flex gap-2 overflow-x-auto pb-1">
						{photos.map((url, index) => {
							const isActive = index === activeIndex
							return (
								<button
									type="button"
									key={`${keyPrefix}-thumb-${index}`}
									onClick={() => goTo(index)}
									className={`group relative overflow-hidden rounded-xl border transition ${isActive ? 'border-blue-400 shadow-sm shadow-blue-100' : 'border-slate-200 hover:border-slate-300'}`}
									aria-label={`Zobrazit fotku ${index + 1}`}
								>
									<img
										src={url}
										alt={`náhled ${index + 1}`}
										loading="lazy"
										className="h-14 w-20 bg-slate-100 object-contain p-1"
									/>
									<span
										className={`pointer-events-none absolute inset-0 ring-1 transition ${isActive ? 'ring-blue-400' : 'ring-transparent group-hover:ring-slate-300'}`}
									/>
								</button>
							)
						})}
					</div>
				) : null}
			</div>

			{isFullscreenOpen ? (
				<div
					className="fixed inset-0 z-50 bg-slate-950/95 p-4 backdrop-blur-sm sm:p-8"
					role="dialog"
					aria-modal="true"
					aria-label={`Náhled fotek ${keyPrefix}`}
					onClick={() => setIsFullscreenOpen(false)}
				>
					<div
						className="mx-auto flex h-full w-full max-w-6xl flex-col"
						onClick={event => event.stopPropagation()}
					>
						<div className="mb-3 flex items-center justify-between text-xs text-slate-300">
							<p>
								Foto {activeIndex + 1} z {total}
							</p>
							<div className="flex items-center gap-2">
								<button
									type="button"
									onClick={() => rotateCurrentPhoto(-90)}
									className={PHOTO_ACTION_BUTTON_DARK_CLASS}
								>
									Otočit doleva
								</button>
								<button
									type="button"
									onClick={() => rotateCurrentPhoto(90)}
									className={PHOTO_ACTION_BUTTON_DARK_CLASS}
								>
									Otočit doprava
								</button>
								<a
									href={currentPhoto}
									target="_blank"
									rel="noreferrer"
									className={PHOTO_ACTION_BUTTON_DARK_CLASS}
								>
									Otevřít originál
								</a>
								<button
									type="button"
									onClick={() => setIsFullscreenOpen(false)}
									className={PHOTO_ACTION_BUTTON_DARK_CLASS}
								>
									Zavřít
								</button>
							</div>
						</div>

						<div
							className="relative flex min-h-0 flex-1 items-center justify-center overflow-auto rounded-2xl border border-slate-700 bg-slate-900/70"
							onTouchStart={onTouchStart}
							onTouchEnd={onTouchEnd}
						>
							<img
								src={currentPhoto}
								alt={`foto vozidla ${activeIndex + 1}`}
								className="max-h-full max-w-full origin-center object-contain p-3 transition-transform duration-200 sm:p-6"
								style={{
									transform: currentPhotoRotation
										? `rotate(${currentPhotoRotation}deg)`
										: undefined,
								}}
							/>

							{hasMultiple ? (
								<>
									<button
										type="button"
										onClick={() => goTo(activeIndex - 1)}
										className="absolute left-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-slate-900/80 text-sm font-bold text-white shadow-lg transition hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
										aria-label="Předchozí fotka"
									>
										&lt;
									</button>
									<button
										type="button"
										onClick={() => goTo(activeIndex + 1)}
										className="absolute right-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-slate-900/80 text-sm font-bold text-white shadow-lg transition hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
										aria-label="Další fotka"
									>
										&gt;
									</button>
								</>
							) : null}
						</div>

						{hasMultiple ? (
							<div className="mt-3 flex justify-center gap-2">
								{photos.map((_, index) => (
									<button
										type="button"
										key={`${keyPrefix}-dot-${index}`}
										onClick={() => goTo(index)}
										className={`h-2.5 rounded-full transition ${index === activeIndex ? 'w-7 bg-blue-400' : 'w-2.5 bg-slate-600 hover:bg-slate-500'}`}
										aria-label={`Přejít na fotku ${index + 1}`}
									/>
								))}
							</div>
						) : null}
					</div>
				</div>
			) : null}
		</>
	)
}

const renderValue = (value: unknown, key = '') => {
	const photos = asPhotoList(value)
	if (photos) {
		const label =
			key === VEHICLE_PHOTO_KEY ? 'Fotodokumentace vozidla' : toFieldLabel(key || 'foto')
		return <VehiclePhotosSlideshow photos={photos} keyPrefix={key || 'photo'} label={label} />
	}

	if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
		return <div className="text-sm text-slate-800">{formatScalarValue(value, key)}</div>
	}

	if (value === null || value === undefined) {
		return <div className="text-sm text-slate-500">neuvedeno</div>
	}

	if (Array.isArray(value)) {
		if (!value.length) return <div className="text-sm text-slate-500">neuvedeno</div>
		return (
			<div className="space-y-2">
				{value.map((item, index) => (
					<div
						key={`${key}-${index}`}
						className="rounded-lg border border-slate-200 bg-white p-2"
					>
						<p className="mb-1 text-xxs font-semibold text-slate-500">
							Položka {index + 1}
						</p>
						{renderValue(item, key)}
					</div>
				))}
			</div>
		)
	}

	if (isObjectRecord(value)) {
		const entries = Object.entries(value).filter(
			([childKey]) => !isFuelHiddenInnerField(key, childKey),
		)
		if (!entries.length) return <div className="text-sm text-slate-500">neuvedeno</div>

		if (typeof value.ok === 'boolean' && entries.length === 1) {
			return (
				<span
					className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${value.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}
				>
					{value.ok ? 'Ano' : 'Ne'}
				</span>
			)
		}

		return (
			<div className="space-y-2">
				{entries.map(([childKey, childValue]) => (
					<div key={childKey} className="rounded-lg border border-slate-200 bg-white p-2">
						<p className="mb-1 text-xxs font-semibold text-slate-500">
							{toFieldLabel(childKey)}
						</p>
						{renderValue(childValue, childKey)}
					</div>
				))}
			</div>
		)
	}

	return <div className="text-sm text-slate-800">{formatScalarValue(String(value), key)}</div>
}

const requestSortWeight = (request: ReservationWorkflowRequest) => {
	const ts = Date.parse(request.updated_at ?? request.created_at ?? '')
	if (Number.isFinite(ts)) return ts
	const parsedId = Number(request.id)
	return Number.isFinite(parsedId) ? parsedId : 0
}

const getLatestByType = (
	requests: ReservationWorkflowRequest[],
	type: ReservationWorkflowRequestType,
) => {
	const list = requests.filter(item => item.request_type === type)
	if (!list.length) return null
	return list.reduce((best, current) =>
		requestSortWeight(current) > requestSortWeight(best) ? current : best,
	)
}

function RequestTypePanel({
	type,
	request,
	isMutating,
	onApprove,
	onReject,
}: {
	type: ReservationWorkflowRequestType
	request: ReservationWorkflowRequest | null
	isMutating: boolean
	onApprove: (id: string) => void
	onReject: (request: ReservationWorkflowRequest) => void
}) {
	if (!request) {
		return (
			<Paper className="rounded-2xl border border-slate-200 p-4">
				<div className="flex items-center justify-between gap-3">
					<h5 className="text-base font-bold text-slate-800">
						{REQUEST_TYPE_LABEL[type]}
					</h5>
					<span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
						Bez záznamu
					</span>
				</div>
				<p className="mt-3 text-sm text-slate-500">
					Zatím nebyl odeslán žádný požadavek pro tuto část.
				</p>
			</Paper>
		)
	}

	const formData = getFormData(request.payload)
	const noteInData =
		typeof formData.note === 'string' && formData.note.trim().length
			? formData.note.trim()
			: null
	const note = noteInData ?? request.note
	const entries = Object.entries(formData)
		.filter(([key]) => key !== 'note' && !isFuelHiddenField(key))
		.sort((a, b) => {
			const aIndex = FIELD_ORDER.indexOf(a[0])
			const bIndex = FIELD_ORDER.indexOf(b[0])
			if (aIndex === -1 && bIndex === -1) return a[0].localeCompare(b[0], 'cs')
			if (aIndex === -1) return 1
			if (bIndex === -1) return -1
			return aIndex - bIndex
		})
	const isPending = request.status === 'pending'

	return (
		<Paper
			className={`rounded-2xl border border-slate-200 border-l-4 p-0 shadow-sm ${STATUS_BORDER[request.status]}`}
		>
			<div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 bg-slate-50/80 px-4 py-3">
				<div>
					<div className="flex items-center gap-2">
						<h5 className="text-base font-bold text-slate-800">
							{REQUEST_TYPE_LABEL[type]}
						</h5>
						<span
							className={`rounded-full px-2 py-1 text-xs font-semibold ring-1 ${STATUS_BADGE[request.status]}`}
						>
							{STATUS_LABEL[request.status]}
						</span>
					</div>
					<div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-500">
						<span>
							Čas: <DateFormatter date={request.created_at ?? undefined} />
						</span>
						<span>
							Odeslal: {request.requested_by_role === 'admin' ? 'Admin' : 'User'}
						</span>
					</div>
				</div>

				{isPending ? (
					<div className="flex flex-wrap gap-2">
						<Button
							size="SMALL"
							variant="PRIMARY"
							isDisabled={isMutating}
							onPress={() => onApprove(request.id)}
						>
							Schválit
						</Button>
						<Button
							size="SMALL"
							variant="DANGER"
							isDisabled={isMutating}
							onPress={() => onReject(request)}
						>
							Zamítnout
						</Button>
					</div>
				) : null}
			</div>

			<div className="space-y-4 p-4">
				{note ? (
					<div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
						<span className="font-semibold">Poznámka:</span> {note}
					</div>
				) : null}

				{request.reason ? (
					<div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
						<span className="font-semibold">Důvod zamítnutí:</span> {request.reason}
					</div>
				) : null}

				{entries.length ? (
					<div className="grid gap-3 md:grid-cols-2">
						{entries.map(([field, value]) => {
							const photos = asPhotoList(value)
							return (
								<Paper
									key={field}
									className={`border-slate-200 bg-slate-50 p-3 ${photos ? 'md:col-span-2' : ''}`}
								>
									<p className="mb-2 text-xs font-semibold text-slate-500">
										{toFieldLabel(field)}
									</p>
									{renderValue(value, field)}
								</Paper>
							)
						})}
					</div>
				) : (
					<div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
						Pro tuto část nejsou vyplněná žádná data.
					</div>
				)}
			</div>
		</Paper>
	)
}

function ReservationWorkflowRequestsContent({
	reservationId,
	title,
}: {
	reservationId: string
	title: string
}) {
	const canConfirmReservation = useHasAccountPermission(ACCOUNT_PERMISSION.CONFIRM_RESERVATION)
	const [requestToReject, setRequestToReject] = useState<ReservationWorkflowRequest | null>(null)
	const [rejectReason, setRejectReason] = useState('')
	const { data, isLoading } = useObjectList('reservation-workflow-request', {
		reservation_id: reservationId,
		page: 1,
		orderKey: 'id',
		orderDir: 'DESC',
	})
	const { mutate: approve, isPending: isApproving } = useObjectActionMutationVariable(
		'reservation-workflow-request',
		'approve',
		[['api', 'reservation']],
		{
			success: () => 'Požadavek schválen',
		},
	)
	const onApprove = (id: string) => approve({ id })
	const { mutate: reject, isPending: isRejecting } = useObjectActionMutationVariable(
		'reservation-workflow-request',
		'reject',
		[['api', 'reservation']],
		{
			success: () => 'Požadavek zamítnut',
		},
	)
	const onReject = () => {
		if (!requestToReject) return

		reject(
			{
				id: requestToReject.id,
				reason: rejectReason.trim() || null,
			},
			{
				onSuccess() {
					setRequestToReject(null)
					setRejectReason('')
				},
			},
		)
	}
	const onAttemptReject = (request: ReservationWorkflowRequest) => {
		setRequestToReject(request)
		setRejectReason('')
	}
	const requests = (data?.list ?? []) as unknown as ReservationWorkflowRequest[]
	const isMutating = isApproving || isRejecting

	const handoverRequest = useMemo(() => getLatestByType(requests, 'handover'), [requests])
	const returnRequest = useMemo(() => getLatestByType(requests, 'return'), [requests])
	const pendingCount = useMemo(
		() => [handoverRequest, returnRequest].filter(item => item?.status === 'pending').length,
		[handoverRequest, returnRequest],
	)

	if (!canConfirmReservation) {
		return (
			<Paper className="mt-4 p-4">
				<p className="text-sm text-slate-600">Pro tuto sekci nemáte oprávnění.</p>
			</Paper>
		)
	}

	return (
		<div className="mt-4 space-y-4">
			<Paper className="overflow-hidden border-slate-200">
				<div className="bg-gradient-to-r from-slate-50 via-white to-blue-50 p-4">
					<div className="flex flex-wrap items-start justify-between gap-3">
						<div>
							<h4 className="text-base font-bold text-slate-800">{title}</h4>
							<p className="mt-1 text-xs text-slate-500">
								Každá rezervace má jednu část pro výdej a jednu pro vratku.
							</p>
						</div>
						{pendingCount ? (
							<span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
								Čeká na rozhodnutí: {pendingCount}
							</span>
						) : null}
					</div>

					<div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
						<div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
							<p className="text-xxs font-semibold text-slate-500">Výdej</p>
							<p className="text-sm font-bold text-slate-800">
								{handoverRequest
									? STATUS_LABEL[handoverRequest.status]
									: 'Bez záznamu'}
							</p>
						</div>
						<div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
							<p className="text-xxs font-semibold text-slate-500">Vratka</p>
							<p className="text-sm font-bold text-slate-800">
								{returnRequest ? STATUS_LABEL[returnRequest.status] : 'Bez záznamu'}
							</p>
						</div>
						<div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
							<p className="text-xxs font-semibold text-slate-500">Rezervace</p>
							<p className="text-sm font-bold text-slate-800">{reservationId}</p>
						</div>
						<div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
							<p className="text-xxs font-semibold text-slate-500">Čeká</p>
							<p className="text-sm font-bold text-amber-700">{pendingCount}</p>
						</div>
					</div>
				</div>
			</Paper>

			{isLoading ? (
				<Paper className="p-6">
					<div className="flex justify-center">{LOADING}</div>
				</Paper>
			) : (
				<div className="grid gap-4 xl:grid-cols-2">
					<RequestTypePanel
						type="handover"
						request={handoverRequest}
						isMutating={isMutating}
						onApprove={onApprove}
						onReject={onAttemptReject}
					/>
					<RequestTypePanel
						type="return"
						request={returnRequest}
						isMutating={isMutating}
						onApprove={onApprove}
						onReject={onAttemptReject}
					/>
				</div>
			)}

			{requestToReject ? (
				<Dialog
					heading="Zamítnout ATD požadavek"
					isOpen
					onOpenChange={() => setRequestToReject(null)}
				>
					<div className="space-y-4 p-4">
						<p className="text-sm text-slate-700">
							{REQUEST_TYPE_LABEL[requestToReject.request_type]} #{requestToReject.id}{' '}
							bude označen jako zamítnutý.
						</p>
						<Textarea
							label="Důvod zamítnutí (volitelné)"
							value={rejectReason}
							onChange={setRejectReason}
							textareaProps={{
								rows: 4,
								placeholder: 'Např. Nesouhlasí stav vozidla',
							}}
						/>
					</div>
					<DialogFooter>
						<Button variant="SECONDARY" onPress={() => setRequestToReject(null)}>
							Zrušit
						</Button>
						<Button variant="DANGER" isPending={isRejecting} onPress={onReject}>
							Potvrdit zamítnutí
						</Button>
					</DialogFooter>
				</Dialog>
			) : null}
		</div>
	)
}

export function ReservationWorkflowRequestsRoute() {
	const { reservationId = '' } = useParams()
	return (
		<ReservationWorkflowRequestsContent reservationId={reservationId} title="Výdej a vratka" />
	)
}
